import type { AxiosInstance, AxiosResponse } from 'axios';
import type Stripe from 'stripe';
import type {
  PostAdminReportsBody,
  PostAdminReportsResponse,
  PutAdminReportsIdBody,
} from '@shared/types/admin';
import type {
  GetApiAccessResponse,
  PutApiAccessResponse,
} from '@shared/types/api-access';
import type {
  GetAppBillingPlansParams,
  GetAppBillingPlansResponse,
} from '~/api';
import type { AccountCustomer, AccountUser } from '~/api/account';
import type { SpendItem } from '~/api/admin';
import type { GetCodesCode } from '~/api/codes';
import type { PostDownloadBody, PostDownloadResponse } from '~/api/downloads';
import type { PostAppPasswordChangeProps } from '~/api/password';
import type { GetSearchQueryParams } from '~/api/search';
import type { PutSearchesResponse } from '~/api/searches';
import type { FulfillmentResults } from '~/api/stripe';
import type { GetSuggesionsCategoriesResponse } from '~/api/suggestions';
import type { TechnologyFacets } from '~/components/TechnologyFacetsTable';
import type { AdminReport, AppAnalyticsEvents } from '@shared/types';
import getAxiosInstance from '~/lib/axios';
import type { AccountsResponse } from '~/routes/admin_.accounts';
import isServer from '~/scripts/isServer';
import { logger } from '~/scripts/logger';
import type {
  GetDatasetsTagResponse,
  GetDatasetWebsites,
} from '@shared/routes/datasets';
import type { GuestHistoryItem } from '~/scripts/searchHistory';
import type {
  Domains,
  Downloads,
  Features,
  Pricing,
  Reports,
  Search,
  Searches,
  Suggestions,
  Users,
  UsersAlerts2,
} from '~/types';

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'DELETE';

export interface ApiEndpoint<
  TResponse = unknown,
  TQuery = never,
  TParams = never,
  TBody = never,
> {
  response: TResponse;
  query?: TQuery extends never ? never : TQuery;
  params?: TParams extends never ? never : TParams;
  body?: TBody extends never ? never : TBody;
}

type ValidHttpEndpoint = {
  [K in HttpMethod]?: ApiEndpoint<unknown, unknown, unknown, unknown>;
};

type ValidateEndpoints<T> = {
  [K in keyof T]: T[K] extends ValidHttpEndpoint ? T[K] : never;
};

export type Endpoints = ValidateEndpoints<{
  '/app/users/me': {
    GET: ApiEndpoint<{
      user: Users.FullUser;
      posthogIdentityHash: string | null;
    }>;
    PUT: ApiEndpoint<
      {},
      never,
      never,
      {
        firstname?: string;
        lastname?: string;
        department?: Users.DEPARTMENT;
        referral?: string;
        company?: string;
      }
    >;
  };
  '/app/account/users': {
    GET: ApiEndpoint<{ users: AccountUser[] }>;
    POST: ApiEndpoint<AccountUser, never, never, { email: string }>;
  };
  '/app/account/customers': {
    GET: ApiEndpoint<AccountCustomer>;
  };
  '/app/account/invoices': {
    GET: ApiEndpoint<Stripe.ApiList<Stripe.Invoice>>;
  };
  '/app/features': {
    GET: ApiEndpoint<{
      features: Features.FeaturesAndVariables;
      apiVersion: string;
      apiCommit: string;
    }>;
  };
  '/admin/reports': {
    GET: ApiEndpoint<{ reports: AdminReport[] }>;
    POST: ApiEndpoint<
      PostAdminReportsResponse,
      never,
      never,
      PostAdminReportsBody
    >;
  };
  '/app/search/facets': {
    GET: ApiEndpoint<{ facets: Search.Facets }>;
  };
  '/admin/reports/:reportId': {
    PUT: ApiEndpoint<
      { ok: boolean },
      never,
      { reportId: number },
      PutAdminReportsIdBody
    >;
  };
  '/admin/tech-spend': {
    GET: ApiEndpoint<SpendItem[]>;
  };
  '/admin/tech-spend/:id': {
    PUT: ApiEndpoint<
      {},
      never,
      { id: number },
      { id: number; min: number; max: number; pricing_url: string }
    >;
  };
  '/admin/domain_logo': {
    GET: ApiEndpoint<
      { name: string; domain: string }[],
      {
        domain: string;
      }
    >;
  };
  '/admin/accounts/:accountsId/block': {
    PUT: ApiEndpoint<{}, never, { accountsId: number }>;
  };
  '/admin/accounts/:accountsId/unblock': {
    PUT: ApiEndpoint<{}, never, { accountsId: number }>;
  };
  '/admin/solr/tags': {
    POST: ApiEndpoint<
      {
        domain: string;
        url: string;
        tags: string[];
        operation: 'inserted' | 'updated' | 'removed';
      }[],
      never,
      never,
      {
        domains?: string[];
        tags: string[];
        action: 'set' | 'add' | 'remove';
      }
    >;
  };
  '/alerts2': {
    GET: ApiEndpoint<UsersAlerts2.Alerts2GetResponse>;
    POST: ApiEndpoint<
      UsersAlerts2.PutAlerts2Response,
      never,
      never,
      UsersAlerts2.PutAlerts2Request
    >;
  };
  '/alerts2/:uuid': {
    PUT: ApiEndpoint<
      UsersAlerts2.PutAlerts2Response,
      never,
      { uuid: string },
      UsersAlerts2.PutAlerts2Request
    >;
  };
  '/app/billing/contact': {
    POST: ApiEndpoint<{}, never, never, { email: string; message: string }>;
    PUT: ApiEndpoint<{}>;
  };
  '/app/account/subscriptions/:subscriptionId': {
    DELETE: ApiEndpoint<true, never, { subscriptionId: string }>;
    PUT: ApiEndpoint<
      true,
      never,
      { subscriptionId: string },
      { resume: boolean }
    >;
  };
  '/app/billing/plans': {
    GET: ApiEndpoint<GetAppBillingPlansResponse, GetAppBillingPlansParams>;
  };
  '/app/checkout/sessions': {
    POST: ApiEndpoint<
      { id: string; client_secret?: string },
      never,
      never,
      {
        plan_id: Pricing.PLAN_ID;
        cancel_url: string;
        users_uuid: string;
        ui_mode: 'embedded' | 'hosted';
      }
    >;
  };
  '/app/events': {
    POST: ApiEndpoint<{}, never, never, { type: AppAnalyticsEvents }>;
  };
  '/codes/:code': {
    GET: ApiEndpoint<GetCodesCode, never, { code: string }>;
  };
  '/admin/crawl/bulk': {
    PUT: ApiEndpoint<{ ok: boolean }, never, never, { domains: string[] }>;
  };
  '/domains/:domain/technologies': {
    GET: ApiEndpoint<Domains.TechnologiesResponse, never, { domain: string }>;
  };
  '/domains/:domain': {
    GET: ApiEndpoint<Search.Site | undefined, never, { domain: string }>;
  };
  '/downloads': {
    POST: ApiEndpoint<PostDownloadResponse, never, never, PostDownloadBody>;
    GET: ApiEndpoint<Downloads.Download[], never, never, never>;
  };
  '/downloads/:hash': {
    GET: ApiEndpoint<Downloads.GetDownloadHash, never, { hash: string }>;
  };
  '/app/leads': {
    POST: ApiEndpoint<
      { ok: boolean },
      never,
      never,
      { email: string; query: string }
    >;
  };
  '/app/password/reset': {
    POST: ApiEndpoint<{ email: string }, never, never, { email: string }>;
  };
  '/app/password/change': {
    POST: ApiEndpoint<true, never, never, PostAppPasswordChangeProps>;
  };
  '/reports': {
    GET: ApiEndpoint<Reports.ReportTotal[]>;
  };
  '/reports/:uuid': {
    GET: ApiEndpoint<Reports.ReportDetails, never, { uuid: string }>;
  };
  '/reports/:uuid/history': {
    GET: ApiEndpoint<
      { history: Reports.ReportHistory[] },
      never,
      { uuid: string }
    >;
  };
  '/searches': {
    PUT: ApiEndpoint<
      PutSearchesResponse,
      never,
      never,
      { search: Searches.StoredSearchObject }
    >;
  };
  '/api-access': {
    GET: ApiEndpoint<GetApiAccessResponse>;
    PUT: ApiEndpoint<PutApiAccessResponse>;
  };
  '/api-access/plans': {
    GET: ApiEndpoint<Stripe.Plan[]>;
  };
  '/api-access/usage': {
    GET: ApiEndpoint<Stripe.UsageRecordSummary[][]>;
  };
  '/app/checkout/save-card': {
    POST: ApiEndpoint<
      { id: string },
      never,
      never,
      { cancel_url: string; success_url: string }
    >;
  };
  '/searches/:uuid': {
    GET: ApiEndpoint<Searches.GetSearch, never, { uuid: string }>;
    PUT: ApiEndpoint<true, never, { uuid: string }>;
  };
  '/searches/history': {
    GET: ApiEndpoint<Searches.GetSearch[]>;
  };
  '/app/checkout/sessions/:session_id/fulfillment': {
    GET: ApiEndpoint<FulfillmentResults, never, { session_id: string }>;
  };
  '/suggestions': {
    GET: ApiEndpoint<Suggestions.Suggestion[], { search: string }>;
  };
  '/suggestions/categories': {
    GET: ApiEndpoint<GetSuggesionsCategoriesResponse>;
  };
  '/overlap/:uuid': {
    GET: ApiEndpoint<GetDatasetWebsites, never, { uuid: string }>;
  };
  '/technologies/:uuid/facets': {
    GET: ApiEndpoint<TechnologyFacets, never, { uuid: string }>;
  };
  '/overlap/:name/:uuid': {
    GET: ApiEndpoint<TechnologyFacets, never, { name: string; uuid: string }>;
  };
  '/search': {
    GET: ApiEndpoint<
      Search.GetSearchResponse,
      GetSearchQueryParams,
      never,
      never
    >;
  };
  '/enrichment': {
    POST: ApiEndpoint<{ ok: boolean }, never, never, { total: number }>;
  };
  '/enrichment/download': {
    POST: ApiEndpoint<{ ok: boolean }, never, never, { total: number }>;
  };
  '/auth/session': {
    POST: ApiEndpoint<
      {},
      never,
      never,
      { grant_type: 'password'; email: string; password: string }
    >;
  };
  '/auth/logout': {
    GET: ApiEndpoint<true>;
  };
  '/auth/users': {
    POST: ApiEndpoint<
      { r?: number | undefined; email_confirmation_required?: boolean },
      never,
      never,
      {
        email: string;
        password: string;
        reason: string;
        name: string;
        referrer?: string;
        email_confirmation_code?: string;
        email_confirmation_required?: boolean;
        landing?: string;
        history?: GuestHistoryItem[] | undefined;
        searchtext?: string;
        /** recaptcha response */
        re?: string;
      }
    >;
  };
  '/auth/:provider/session': {
    POST: ApiEndpoint<
      { isNewUser: boolean },
      never,
      { provider: string },
      {
        code: string;
        history?: GuestHistoryItem[] | undefined;
        referrer?: string;
        landing?: string;
        searchtext?: string;
        reason: string;
      }
    >;
  };
  '/admin/accounts': {
    GET: ApiEndpoint<AccountsResponse>;
  };
  '/admin/industries/:industryId': {
    PUT: ApiEndpoint<{}, never, { industryId: number }, { name: string }>;
  };
  '/admin/categories/:categoryId': {
    PUT: ApiEndpoint<
      {},
      never,
      { categoryId: number },
      { name: string; industries_id: number }
    >;
  };
  '/admin/categories': {
    POST: ApiEndpoint<
      {},
      never,
      never,
      { name: string; industries_id: number }
    >;
  };
  '/admin/industries': {
    POST: ApiEndpoint<{}, never, never, { name: string }>;
  };
  '/datasets/:tag': {
    GET: ApiEndpoint<
      GetDatasetsTagResponse,
      { start?: number; tags?: string[] },
      { tag: string }
    >;
  };
  '/datasets/top-websites': {
    GET: ApiEndpoint<
      {
        total: number;
        sites: Array<{
          domain: string;
          url: string;
          updated_at: string;
          spend_min: number;
          spend_max: number;
        }>;
        start: number;
      },
      { start?: number }
    >;
  };
}>;

export type EndpointKey = keyof Endpoints;

// Modified to better handle the validation chain
type EndpointConfig<T, M extends HttpMethod> = T extends {
  [K in M]: ApiEndpoint<unknown, unknown, unknown, unknown>;
}
  ? T[M]
  : never;

export type RequestConfig<
  TEndpoint extends EndpointKey,
  TMethod extends HttpMethod,
> = TMethod extends keyof Endpoints[TEndpoint]
  ? {
      query?: EndpointConfig<Endpoints[TEndpoint], TMethod>['query'];
      params?: EndpointConfig<Endpoints[TEndpoint], TMethod>['params'];
      body?: EndpointConfig<Endpoints[TEndpoint], TMethod>['body'];
      headers?: Record<string, string>;
      /** Timeout in milliseconds */
      timeout?: number;
      signal?: AbortSignal;
    }
  : never;

type ResponseType<
  TEndpoint extends EndpointKey,
  TMethod extends HttpMethod,
> = TMethod extends keyof Endpoints[TEndpoint]
  ? EndpointConfig<Endpoints[TEndpoint], TMethod>['response']
  : never;

export class ApiClient {
  private axiosInstance: AxiosInstance;

  constructor(request?: Request) {
    this.axiosInstance = getAxiosInstance(request);
  }

  async requestBody<TEndpoint extends EndpointKey, TMethod extends HttpMethod>(
    endpoint: TEndpoint,
    method: TMethod,
    config?: RequestConfig<TEndpoint, TMethod>
  ): Promise<ResponseType<TEndpoint, TMethod>> {
    return this.request(endpoint, method, config).then(
      (response) => response.data
    );
  }

  async request<TEndpoint extends EndpointKey, TMethod extends HttpMethod>(
    endpoint: TEndpoint,
    method: TMethod,
    config?: RequestConfig<TEndpoint, TMethod>
  ): Promise<AxiosResponse<ResponseType<TEndpoint, TMethod>>> {
    const { params, query, body } = config || {};

    let url: string = endpoint;
    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        url = url.replace(`:${key}`, value.toString());
      });
    }

    const requestConfig = {
      url,
      method,
      params: query,
      data: body,
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
        ...config?.headers,
      },
      withCredentials: true,
      timeout: config?.timeout,
      signal: config?.signal,
    };
    logger.debug(
      `[API Request] --> ${requestConfig.method} ${requestConfig.url}`
    );
    const response = await this.axiosInstance
      .request(requestConfig)
      .catch((response) => {
        if (isServer()) {
          //
        } else if (location.href.indexOf('debug') > -1) {
          // eslint-disable-next-line no-debugger
          debugger;
        }
        throw response;
      });
    logger.debug(
      `[API Response] <-- ${requestConfig.method} ${requestConfig.url} - ${response.status}`
    );

    return response;
  }

  async get<TEndpoint extends EndpointKey>(
    endpoint: TEndpoint,
    config?: RequestConfig<TEndpoint, 'GET'>
  ) {
    return this.requestBody(endpoint, 'GET', config);
  }

  async put<TEndpoint extends EndpointKey>(
    endpoint: TEndpoint,
    config?: RequestConfig<TEndpoint, 'PUT'>
  ) {
    return this.requestBody(endpoint, 'PUT', config);
  }

  async post<TEndpoint extends EndpointKey>(
    endpoint: TEndpoint,
    config?: RequestConfig<TEndpoint, 'POST'>
  ) {
    return this.requestBody(endpoint, 'POST', config);
  }

  async delete<TEndpoint extends EndpointKey>(
    endpoint: TEndpoint,
    config?: RequestConfig<TEndpoint, 'DELETE'>
  ) {
    return this.requestBody(endpoint, 'DELETE', config);
  }
}

const api = new ApiClient();
export default api;
